import { Reducers, ReduxReusableComponents, RRCProps, State } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { createTestids } from "@famiprog-foundation/tests-are-demo";
import React from "react";
import { Button, Header, Input, Label, Popup, SemanticCOLORS, StrictButtonProps } from "semantic-ui-react";
import { PopupWithHelpTooltip } from "../semanticUiReactExt";

export const advancedSelectorTestids = createTestids("AdvancedSelector", {
    item: ""
});

export type AdvancedSelectorItem = {
    id: number | string,
    level: number,
    text: string,
    color: SemanticCOLORS | undefined,
    // Used only by elements on first level
    icon?: string,
    data?: any,
    className?: string,
    // Used only by elements on third level
    disabled?: boolean
    // Used only by disabled elements on third level
    tooltip?: string
}

export class AdvancedSelectorState extends State {
    searchedValue: string = "";
}

export class AdvancedSelectorReducers<S extends AdvancedSelectorState = AdvancedSelectorState> extends Reducers<S> {
    update(searchedValue: string) {
        this.s.searchedValue = searchedValue;
    }
}

type PropsNotFromState = {
    data: AdvancedSelectorItem[],
    searchActionProps?: StrictButtonProps,
    onItemClick: (data: AdvancedSelectorItem) => void,
    onSearchedValueChanged?: (inputSearchValue: string) => void,
    renderItemFunction?: (data: AdvancedSelectorItem, callback: (data: AdvancedSelectorItem) => void) => React.ReactNode
};
type Props = RRCProps<AdvancedSelectorState, AdvancedSelectorReducers> & PropsNotFromState;

export class AdvancedSelector extends React.Component<Props, { inputValue: string, searchColor: SemanticCOLORS }> {

    static defaultProps = {
        searchActionProps: { icon: "search" }
    }

    constructor(props: Props) {
        super(props);
        this.onSearchKeyDown = this.onSearchKeyDown.bind(this);
        this.state = {
            inputValue: "",
            searchColor: "green"
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<{ inputValue: string; searchColor: SemanticCOLORS; }>, snapshot?: any): void {
        if (prevProps.s.searchedValue !== this.props.s.searchedValue) {
            this.props.onSearchedValueChanged && this.props.onSearchedValueChanged(this.props.s.searchedValue);
        }
    }

    private renderItem(item: AdvancedSelectorItem, dataTestId: string) {
        if (item.level === 3 && !item.text?.toUpperCase().includes(this.props.s.searchedValue.toUpperCase())) {
            return;
        }
        const result = this.props.renderItemFunction?.call(null, item, this.props.onItemClick);
        if (result) {
            return result;
        }

        const extraClassName = item.className ? item.className : "";

        switch (item.level) {
            case 1:
                return <Header icon={item.icon ? item.icon : undefined} data-testid={dataTestId} color={item.color}
                    className={"small-margin-bottom " + extraClassName} as="h4" key={item.id} content={item.text} />;
            case 2:
                return <Label data-testid={dataTestId} className={"small-margin-bottom " + extraClassName} key={item.id} color={item.color}>{item.text}</Label>;
            case 3:
                return <Popup disabled={!item.disabled} content={item.disabled ? item.tooltip : undefined} trigger={
                    <span><Button disabled={item.disabled} color={item.color}
                        data-testid={dataTestId} compact size="tiny" key={item.id} className={"small-margin-bottom " + extraClassName}
                        onClick={() => this.props.onItemClick(item)}>{item.text}</Button></span>
                } />;
            default:
                return <></>;
        }
    }

    checkItemsNumberAndClick() {
        // Here we use inputValue in order to have the updated value from input
        const items = this.props.data.filter(item => item.level === 3 && item.text?.toUpperCase().includes(this.state.inputValue.toUpperCase()));
        if (items.length === 1) {
            this.props.onItemClick(items[0]);
        }
    }

    private onSearchKeyDown(e: any, value: any) {
        if (e.key === 'Enter') {
            // Disable the default behavior for one item on enter, if we have a method that listens for changes on searchedValue
            if (!this.props.onSearchedValueChanged) {
                this.checkItemsNumberAndClick();
            }
            this.props.r.update(this.state.inputValue);
            this.setState({ searchColor: "green" });
        }
    }

    render() {
        return <div className="flex-grow-shrink-no-overflow">
            <div className="flex-container-row flex-center">
                <Input className="AdvancedSelector_searchInput" fluid autoFocus action value={this.state.inputValue} onKeyDown={this.onSearchKeyDown} onChange={e => {
                    this.setState({ inputValue: e.target.value, searchColor: "purple" });
                }}>
                    <input/>
                    <Button color={this.state.searchColor} onClick={(e: any) => {
                        this.setState({ searchColor: "green" });
                        this.props.r.update(this.state.inputValue);
                    }} {...this.props.searchActionProps} />
                    <Button icon="delete" color="red" onClick={(e: any) => {
                        this.setState({ inputValue: "", searchColor: "green" });
                        this.props.r.update("");
                    }} />
                </Input>
                <PopupWithHelpTooltip defaultPopupPosition tooltip={_msg("AdvancedSelector.infoTooltip.label")} />
            </div>
            {this.props.data.map((item, index) => this.renderItem(item, advancedSelectorTestids.item + "_" + index))}
        </div>
    }
}

export const AdvancedSelectorRRC = ReduxReusableComponents.connectRRC(AdvancedSelectorState, AdvancedSelectorReducers, AdvancedSelector);
"../../reduxReusableComponents/ReduxReusableComponents"